import {
    AbsenceInput,
    AuxiliarySalaryInput,
    BonusInput,
    DynamicFee,
    DynamicFeeInput,
    EffectiveFee,
    EffectiveFeeInput,
    ProfileType,
} from "../../../__generated__/graphql";
import {
    CoreMissionFieldsFragment,
    GetMissionVacationStatusQuery,
    MissionPayrollBreakdownFieldsFragment,
} from "../../../__generated__/operations/graphql";
import { StackNavigationProp } from "@react-navigation/stack";
import { RootStackParamList } from "../../../navigations/RootNavigation";
import { useCallback } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import { CustomRowsType } from "../../../screens/client/PayrollBreakdownEditorScreen";

export type FormData = {
    usedVacationDays?: number;
    totalWorkedDays?: number;
    absences: AbsenceInput[];
    bonuses: BonusInput[];
    auxiliarySalaries: AuxiliarySalaryInput[];
    effectiveFees: EffectiveFee[];
    dynamicFees: DynamicFee[];

    totalWork100Hours?: number;
    totalWork115Hours?: number;
    totalWork125Hours?: number;
    totalWork150Hours?: number;
    totalWork200Hours?: number;

    deltaWorkHours?: number;
    deltaWorkMode?: "plus" | "minus";

    overtimePaid100Hours?: number;
    overtimePaid125Hours?: number;
};

export type PayrollBreakdownData = {
    deltaWork?: number; // in minutes
    usedVacationMinutes?: number; // in minutes
    usedVacationAmount?: number; // in cents
    totalWorkedDays?: number; // in days
    totalWork100?: number; // in minutes
    totalWork115?: number; // in minutes
    totalWork125?: number; // in minutes
    totalWork150?: number; // in minutes
    totalWork200?: number; // in minutes
    absences?: Array<AbsenceInput>;
    bonuses?: Array<BonusInput>;
    auxiliarySalaries?: Array<AuxiliarySalaryInput>;
    effectiveFees?: Array<EffectiveFeeInput>;
    dynamicFees?: Array<DynamicFeeInput>;
    overtimePaid100?: number;
    overtimePaid125?: number;
};

export type PayrollBreakdownEditorProps = {
    payrollBreakdown: MissionPayrollBreakdownFieldsFragment;
    mission?: CoreMissionFieldsFragment;
    vacationStatus: GetMissionVacationStatusQuery["getMissionVacationStatus"];
    navigation?: StackNavigationProp<RootStackParamList, "PayrollBreakdownEditor">;
    profileType: ProfileType;
    needWorkDays: boolean;
    isFromTimesheet: boolean;
    onValidData: (data: PayrollBreakdownData | null) => void;
    onChange: (data: PayrollBreakdownData) => void;
    setCustomSummaryRows: (customRows: CustomRowsType[]) => void;
    timeSheetsNeedCompletion?: boolean;
    isEmptyPeriod?: boolean;
};

export const useValidateDecimal = (isMandatory = true) => {
    const i18n = useI18n();
    return useCallback(
        (value: string | null | undefined) => {
            if (isMandatory && (value === undefined || value === "")) {
                return i18n.t("payroll_breakdown.error_not_set");
            }
            if (value && isNaN(Number(value))) {
                return i18n.t("payroll_breakdown.error_not_a_number");
            }
            return undefined;
        },
        [i18n, isMandatory],
    );
};

export const calculatePayrollBreakdownMaxWorkDays = ({
    contractStart,
    contractEnd,
    payrollBreakdownStart,
    payrollBreakdownEnd,
}: {
    contractStart?: string | null;
    contractEnd?: string | null;
    payrollBreakdownStart: string;
    payrollBreakdownEnd: string;
}) => {
    let max = 31; // 1 month by default
    if (contractEnd && contractStart) {
        max =
            Math.ceil(
                (new Date(contractEnd).getTime() - new Date(contractStart).getTime()) /
                    (1000 * 60 * 60 * 24),
            ) + 1; // +1 to include the last day
    }
    const payrollBreakdownDays = Math.ceil(
        (new Date(payrollBreakdownEnd).getTime() - new Date(payrollBreakdownStart).getTime()) /
            (1000 * 60 * 60 * 24) +
            1,
    );
    return Math.min(payrollBreakdownDays, max);
};
